import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import Icon from 'dpl/common/components/Icon';
import { PAGE } from 'dpl/constants/query_params';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import { addToQuery, removeFromUrl } from 'dpl/util/queryString';

const IS_LT_MD = isLessThanBreakpoint('md');
const ICON_SIZE = IS_LT_MD ? '20px' : '24px';
const PAGE_SEQUENCE_MAX_COUNT = 6;
const PAGE_RANGE_GAP = '...';

const PAGE_LINK_TYPES = {
  NUMBER: 'number',
  NEXT: 'next',
  PREV: 'prev'
};

function getPageUrl(pageNumber) {
  if (pageNumber > 1) {
    return `?${addToQuery({ [PAGE]: pageNumber }, window.location.search)}`;
  }

  return removeFromUrl(PAGE, window.location.href);
}

function getPageLinkAriaLabel(
  type = PAGE_LINK_TYPES.NUMBER,
  currentPageNumber,
  pageNumber,
  totalPageCount
) {
  if (type === PAGE_LINK_TYPES.NEXT) {
    return `Go to next page, page ${currentPageNumber + 1}`;
  }

  if (type === PAGE_LINK_TYPES.PREV) {
    return `Go to previous page, page ${currentPageNumber - 1}`;
  }

  if (pageNumber === currentPageNumber) {
    return `Current page, page ${pageNumber}`;
  }

  const isFirstPageNumber = pageNumber === 1;
  const isLastPageNumber = pageNumber === totalPageCount;

  return [
    `Go to page ${pageNumber}`,
    totalPageCount > 1 && isFirstPageNumber && `(first page)`,
    totalPageCount > 1 && isLastPageNumber && `(last page)`
  ]
    .filter(Boolean)
    .join(' ');
}

function generatePageLinkLabels(totalPageCount, currentPageNumber) {
  /*
    This method generates an array of page link labels, including page numbers and range gaps,
    to be used in the paginator's button links. The first and last page numbers will always be included.
    
    Examples:
    - generatePageLinkLabels(6, 1);   // [1, 2, 3, 4, 5, 6]
    - generatePageLinkLabels(30, 1)   // [1, 2, 3, 4, '...', 30]
    - generatePageLinkLabels(10, 5);  // [1, '...', 4, 5, 6, '...', 10]
  */

  if (totalPageCount <= PAGE_SEQUENCE_MAX_COUNT || window.__DPL_BOT_UA) {
    // Return a numeric sequence array from 1 to totalPageCount
    return Array.from({ length: totalPageCount }).map((_, i) => i + 1);
  }

  let pageLinkLabels = [];

  // Set the middle pagination numbers
  if (currentPageNumber <= 3) {
    pageLinkLabels = [2, 3, 4];
  } else if (currentPageNumber > totalPageCount - 3) {
    pageLinkLabels = [4, 3, 2].map(n => totalPageCount - n);
  } else {
    pageLinkLabels = [-1, 0, 1].map(n => currentPageNumber + n);
  }

  const firstPageNumber = pageLinkLabels[0];
  const lastPageNumber = pageLinkLabels[pageLinkLabels.length - 1];

  if (firstPageNumber === 3) {
    // Since 1 and the total page number will always be included in the result, if the first page
    // number in the array equals 3, insert 2 (instead of a page range gap)
    pageLinkLabels.unshift(2);
  } else if (firstPageNumber > 3) {
    // If the difference in page numbers would be greater than 1, insert a page range gap
    pageLinkLabels.unshift(PAGE_RANGE_GAP);
  }

  // Same as the start of the array: insert a page range gap only when the adjacent numbers
  // wouldn't be consecutive.
  if (totalPageCount - lastPageNumber === 2) {
    pageLinkLabels.push(lastPageNumber + 1);
  } else if (totalPageCount - lastPageNumber > 1) {
    pageLinkLabels.push(PAGE_RANGE_GAP);
  }

  // Insert 1 at the start of the array only if it's not already present
  if (firstPageNumber !== 1) {
    pageLinkLabels.unshift(1);
  }

  // Insert the total page count at the end of the array only if it's not already present
  if (lastPageNumber !== totalPageCount) {
    pageLinkLabels.push(totalPageCount);
  }

  return pageLinkLabels;
}

/** @param {any} props */
function PageLink({
  className = null,
  currentPageNumber,
  disabled = false,
  onClick = null,
  pageNumber,
  pageUrlFor = getPageUrl,
  totalPageCount,
  type = PAGE_LINK_TYPES.NUMBER
}) {
  const isCurrentPage = pageNumber === currentPageNumber;
  const Component = disabled ? 'span' : 'a';

  function handleClick(e) {
    e.preventDefault();
    onClick?.(pageNumber);
  }

  return (
    <Component
      className={classnames(
        'DSPageLink flex items-center justify-center font-serif font-14 md:font-16 fw-medium br-100 lh-0 ba',
        disabled ? 'disabled' : 'stone-700',
        isCurrentPage ? 'b--stone-700' : 'b--transparent',
        className
      )}
      aria-label={getPageLinkAriaLabel(
        type,
        currentPageNumber,
        pageNumber,
        totalPageCount
      )}
      aria-current={isCurrentPage || null}
      href={!disabled ? pageUrlFor(pageNumber) : null}
      onClick={!disabled ? handleClick : null}
    >
      {type === PAGE_LINK_TYPES.NUMBER && pageNumber}
      {type === PAGE_LINK_TYPES.NEXT && (
        <Icon name="fetch-arrow-right" height={ICON_SIZE} width={ICON_SIZE} />
      )}
      {type === PAGE_LINK_TYPES.PREV && (
        <Icon name="fetch-arrow-left" height={ICON_SIZE} width={ICON_SIZE} />
      )}
    </Component>
  );
}

PageLink.propTypes = {
  className: PropTypes.string,
  currentPageNumber: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  pageNumber: PropTypes.number.isRequired,
  pageUrlFor: PropTypes.func,
  totalPageCount: PropTypes.number.isRequired,
  type: PropTypes.oneOf(Object.values(PAGE_LINK_TYPES))
};

function PageRangeGap() {
  return (
    <span className="DSPageRangeGap flex items-center justify-center font-serif font-14 md:font-16 fw-medium lh-0">
      ...
    </span>
  );
}

/** @param {any} props */
export default function Paginator({
  className = null,
  currentPage = 1,
  itemsPerPage = 10,
  onPageClick,
  pageUrlFor = getPageUrl,
  totalItemCount = 0,
  arrows = true
}) {
  const totalPageCount = Math.ceil(totalItemCount / itemsPerPage);
  const pageLinkLabels = generatePageLinkLabels(totalPageCount, currentPage);

  return (
    <nav
      className={classnames('DSPaginator', className)}
      role="navigation"
      aria-label="Pagination navigation"
    >
      <ol className="inline-flex gap-4 md:gap-12">
        {arrows && (
          <li aria-hidden={currentPage === 1 || null}>
            <PageLink
              className="secondary b--default"
              currentPageNumber={currentPage}
              disabled={currentPage === 1}
              onClick={onPageClick}
              pageNumber={currentPage - 1}
              pageUrlFor={pageUrlFor}
              totalPageCount={totalPageCount}
              type={PAGE_LINK_TYPES.PREV}
            />
          </li>
        )}
        {pageLinkLabels.map((pageLinkLabel, index) => (
          <li
            key={index}
            aria-hidden={pageLinkLabel === PAGE_RANGE_GAP || null}
          >
            {pageLinkLabel === PAGE_RANGE_GAP ? (
              <PageRangeGap />
            ) : (
              <PageLink
                currentPageNumber={currentPage}
                onClick={onPageClick}
                pageNumber={pageLinkLabel}
                pageUrlFor={pageUrlFor}
                totalPageCount={totalPageCount}
              />
            )}
          </li>
        ))}
        {arrows && (
          <li aria-hidden={currentPage === totalPageCount || null}>
            <PageLink
              className="secondary b--default"
              currentPageNumber={currentPage}
              disabled={currentPage === totalPageCount}
              onClick={onPageClick}
              pageNumber={currentPage + 1}
              pageUrlFor={pageUrlFor}
              totalPageCount={totalPageCount}
              type={PAGE_LINK_TYPES.NEXT}
            />
          </li>
        )}
      </ol>
    </nav>
  );
}

Paginator.propTypes = {
  className: PropTypes.string,
  currentPage: PropTypes.number,
  itemsPerPage: PropTypes.number,
  onPageClick: PropTypes.func.isRequired,
  // Builds each link's `href`. Only worth passing when the paginated surface
  // does not read `?p=` - an overlay paginating on its own scoped param would
  // otherwise advertise a URL that paginates the page underneath it.
  pageUrlFor: PropTypes.func,
  totalItemCount: PropTypes.number,
  arrows: PropTypes.bool
};
